import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { capturePosthog } from "@/lib/posthog";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const trackHeaderCta = (ctaId: string, ctaLabel: string) => {
    if (ctaId.includes("try_free")) {
      capturePosthog("landing_cta_try_free_clicked", {
        cta_id: ctaId,
        cta_label: ctaLabel,
        cta_section: "header",
      });
    }
    capturePosthog("landing_cta_clicked", {
      cta_id: ctaId,
      cta_label: ctaLabel,
      cta_section: "header",
    });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[backdrop-filter,background,box-shadow,height] duration-300 ${
        scrolled
          ? "glass-pill border-b border-border/50 shadow-[0_8px_32px_-12px_hsl(var(--primary)/0.08)]"
          : "bg-background/0 border-b border-transparent"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className={`flex items-center justify-between transition-[height] duration-300 ${scrolled ? "h-14 md:h-16" : "h-16 md:h-20"}`}>
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-9 h-9 rounded-lg bg-transparent flex items-center justify-center overflow-hidden transition-transform duration-base group-hover:rotate-[-4deg]">
              <img
                src="/logo.webp"
                alt=""
                width={36}
                height={36}
                decoding="async"
                fetchPriority="high"
                className="w-9 h-9 object-contain"
              />
            </div>
            <span className="text-payfit-value text-foreground tracking-tight">Equiply</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {[
              { href: "/#features", label: "Fonctionnalités" },
              { href: "/#pricing", label: "Tarifs" },
              { href: "/#faq", label: "FAQ" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="relative px-4 py-2 text-payfit-body text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-foreground/[0.04]"
              >
                {item.label}
              </a>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-2">
            <Button variant="ghost" asChild className="rounded-full">
              <Link to="/auth" onClick={() => trackHeaderCta("header_login", "Connexion")}>Connexion</Link>
            </Button>
            <Button asChild className="rounded-full px-5 shadow-[0_8px_24px_-8px_hsl(var(--primary)/0.45)] hover:shadow-[0_12px_32px_-8px_hsl(var(--primary)/0.55)] transition-shadow">
              <Link to="/auth?mode=signup" onClick={() => trackHeaderCta("header_try_free", "Essai gratuit")}>Essai gratuit</Link>
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <SheetTrigger asChild>
              <button
                className="md:hidden p-3 -m-1 min-w-[48px] min-h-[48px] flex items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label="Ouvrir le menu"
              >
                <Menu className="w-6 h-6 text-foreground" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              surface="solid"
              className="md:hidden w-[85%] max-w-sm flex flex-col"
            >
              <SheetHeader>
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-1 mt-2">
                {[
                  { href: "/#features", label: "Fonctionnalités" },
                  { href: "/#pricing", label: "Tarifs" },
                  { href: "/#faq", label: "FAQ" },
                ].map((item) => (
                  <SheetClose asChild key={item.href}>
                    <a
                      href={item.href}
                      className="px-3 py-2.5 rounded-xl text-payfit-body text-foreground/80 hover:bg-foreground/5 transition-colors"
                    >
                      {item.label}
                    </a>
                  </SheetClose>
                ))}
              </nav>
              <div className="mt-auto flex flex-col gap-2 pt-6 border-t border-border/50">
                <SheetClose asChild>
                  <Button variant="outline" asChild className="w-full rounded-full">
                    <Link
                      to="/auth"
                      onClick={() => trackHeaderCta("mobile_header_login", "Connexion mobile")}
                    >
                      Connexion
                    </Link>
                  </Button>
                </SheetClose>
                <SheetClose asChild>
                  <Button asChild className="w-full rounded-full">
                    <Link
                      to="/auth?mode=signup"
                      onClick={() => trackHeaderCta("mobile_header_try_free", "Essai gratuit mobile")}
                    >
                      Essai gratuit
                    </Link>
                  </Button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
